import {
  type ResetPasswordInput,
  requestPasswordResetInputSchema,
  resetPasswordInputSchema,
} from '@repo/api-contracts';
import { useState } from 'react';
import { trpc } from '@/shared/api';
import { firstErrorPerField, getErrorMessage, mapServerValidationErrors } from '@/shared/lib';

const REQUEST_FIELD_KEYS = ['email'] as const;

interface UseRequestPasswordResetResult {
  readonly emailError: string | null;
  readonly formError: string | null;
  readonly isPending: boolean;
  readonly isDone: boolean;
  readonly submit: (email: string) => Promise<void>;
}

/** 재설정 메일 요청. 서버는 계정 존재와 무관하게 항상 성공을 돌려준다. (열거 방지) */
export const useRequestPasswordReset = (): UseRequestPasswordResetResult => {
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);
  const mutation = trpc.auth.requestPasswordReset.useMutation();

  const submit = async (email: string): Promise<void> => {
    setFormError(null);

    const parsed = requestPasswordResetInputSchema.safeParse({ email });

    if (!parsed.success) {
      setEmailError(parsed.error.issues[0]?.message ?? '이메일을 확인해 주세요.');
      return;
    }

    setEmailError(null);

    try {
      await mutation.mutateAsync(parsed.data);
      setIsDone(true);
    } catch (error) {
      const serverErrors = mapServerValidationErrors(error, REQUEST_FIELD_KEYS);

      if (serverErrors) {
        setEmailError(serverErrors.fieldErrors.email ?? null);
        setFormError(serverErrors.formError);
      } else {
        setFormError(getErrorMessage(error, '요청에 실패했습니다. 잠시 후 다시 시도해 주세요.'));
      }
    }
  };

  return { emailError, formError, isPending: mutation.isPending, isDone, submit };
};

const RESET_FIELD_KEYS = ['password'] as const;

interface UseResetPasswordResult {
  readonly passwordError: string | null;
  readonly formError: string | null;
  readonly isPending: boolean;
  readonly isDone: boolean;
  readonly submit: (input: ResetPasswordInput) => Promise<void>;
}

/** 메일 링크의 토큰으로 새 비밀번호를 설정한다. 성공 시 전 세션이 폐기된다. */
export const useResetPassword = (): UseResetPasswordResult => {
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);
  const mutation = trpc.auth.resetPassword.useMutation();

  const submit = async (input: ResetPasswordInput): Promise<void> => {
    setFormError(null);

    const parsed = resetPasswordInputSchema.safeParse(input);

    if (!parsed.success) {
      const flattened = firstErrorPerField(parsed.error.flatten().fieldErrors, RESET_FIELD_KEYS);
      setPasswordError(flattened.password ?? '입력을 확인해 주세요.');
      return;
    }

    setPasswordError(null);

    try {
      await mutation.mutateAsync(parsed.data);
      setIsDone(true);
    } catch (error) {
      const serverErrors = mapServerValidationErrors(error, RESET_FIELD_KEYS);

      if (serverErrors) {
        setPasswordError(serverErrors.fieldErrors.password ?? null);
        setFormError(serverErrors.formError);
      } else {
        // 없음 · 만료 · 이미 사용된 링크는 서버가 단일 메시지로 거부한다.
        setFormError(getErrorMessage(error, '재설정에 실패했습니다.'));
      }
    }
  };

  return { passwordError, formError, isPending: mutation.isPending, isDone, submit };
};
